import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo } from "@/i18n";
import { ARTICLES } from "@/data/articles";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";

export function Guides() {
  const { lang } = useLang();
  const t = T[lang];
  const categories = [...new Set(ARTICLES.map((a) => a[lang].category))];
  return (
    <div className="container-site py-28 md:py-36" data-testid="guides-page">
      <SEO title={`${t.nav.guides} — ${t.brand}`} description={t.home.articlesSub} lang={lang} pathKey="guides" />
      <Reveal>
        <p className="eyebrow">{t.nav.guides}</p>
        <h1 className="h2-serif mt-3">{t.home.articlesTitle}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.articlesSub}</p>
      </Reveal>
      {categories.map((cat) => (
        <div key={cat} className="mt-14">
          <h2 className="font-serif text-2xl text-[#B5472B]">{cat}</h2>
          <div className="mt-6 grid gap-8 md:grid-cols-3">
            {ARTICLES.filter((a) => a[lang].category === cat).map((a) => (
              <Link key={a.slug} to={linkTo(lang, "article", a.slug)} className="group" data-testid={`guide-card-${a.slug}`}>
                <div className="overflow-hidden rounded-2xl">
                  <img src={a.img} alt={a[lang].title} className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <h3 className="font-serif text-xl font-semibold mt-4 leading-snug group-hover:text-[#B5472B] transition-colors">{a[lang].title}</h3>
                <p className="mt-2 text-sm text-[#1F2328]/60 leading-relaxed">{a[lang].excerpt}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#0E4D5C]">{t.common.readMore} <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = T[lang];
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) {
    return (
      <div className="container-site py-40 text-center">
        <h1 className="h2-serif">{t.notFound.title}</h1>
        <Link to={linkTo(lang, "guides")} className="btn-primary mt-8">{t.common.back}</Link>
      </div>
    );
  }
  return (
    <article className="py-28 md:py-36" data-testid="article-page">
      <SEO title={`${a[lang].title} — ${t.brand}`} description={a[lang].excerpt} lang={lang} pathKey="article" slug={a.slug} />
      <div className="container-site max-w-3xl">
        <Link to={linkTo(lang, "guides")} className="inline-flex items-center gap-2 text-sm text-[#0E4D5C] hover:text-[#B5472B]"><ArrowLeft size={15} /> {t.nav.guides}</Link>
        <p className="eyebrow mt-8">{a[lang].category}</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0E4D5C] mt-3 leading-tight" data-testid="article-title">{a[lang].title}</h1>
        <p className="mt-4 flex items-center gap-2 text-sm text-[#8A8F96]"><CalendarDays size={14} /> {new Date(a.date).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>
      <div className="container-site max-w-4xl mt-10">
        <img src={a.img} alt={a[lang].title} className="w-full h-72 sm:h-96 object-cover rounded-3xl" loading="lazy" />
      </div>
      <div className="container-site max-w-3xl mt-10 space-y-6 text-lg text-[#1F2328]/80 leading-relaxed" data-testid="article-body">
        {a[lang].body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div className="container-site max-w-3xl mt-14">
        <div className="rounded-3xl bg-[#0E4D5C] text-[#F6EFE4] p-8 text-center">
          <h2 className="font-serif text-2xl">{t.home.ctaTitle}</h2>
          <Link to={linkTo(lang, "contact")} className="btn-primary mt-5" data-testid="article-cta">{t.nav.cta} <ArrowRight size={15} /></Link>
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        headline: a[lang].title, datePublished: a.date, image: a.img, description: a[lang].excerpt,
      })}} />
    </article>
  );
}
